import { useContext, useCallback } from "react"
import { Box, Button, Divider, Typography, useMediaQuery } from "@mui/material"
import AddLinkIcon from '@mui/icons-material/AddLink'
import InstagramIcon from '@mui/icons-material/Instagram'
import { Video } from 'lucide-react'
import {
    DndContext, closestCorners, PointerSensor, TouchSensor, useSensor, useSensors
} from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable"

import { SocialIconsList } from "../../../../components/common/domain/sharable/studio/SocialIconsList"
import { LinkButtonStudio } from "../../../../components/common/domain/sharable/studio/LinkButtonStudio"

import { SocialLinkModal } from "./modals/sociallink/SocialLinkModal"
import { AddLinkButonModal } from "./modals/linkbutton/AddLinkButtonModal"

import { LinksContext } from "../../../../context/LinkProvider"
import { MediaContext } from "../../../../context/MediaProvider"
import SubscriptionAlert from "../../../../utils/subscription/SubscriptionAlert"

const PRO_TIERS = ['PRO_MONTHLY', 'PRO_YEARLY', 'PREMIUM_MONTHLY', 'PREMIUM_YEARLY']

export default function LinksTab({ user }) {
    const {
        pageId,
        linkButtons,
        socialLinks,
        moneyLinks,
        openLinkModal,
        setOpenLinkModal,
        openIconModal,
        setOpenIconModal,
        handleAddLink,
        handleReorderLinks,
        handleUpdateLink,
        handleDeleteLink,
        handleSocialIconChange,
        handleSetMoneyType,
    } = useContext(LinksContext)

    // Money-link tagging is a Pro capability (mirrors the backend's TwoPlanAccessPolicy).
    const isPro = !!user?.hasActiveTier && PRO_TIERS.includes(user?.tier)

    const hasLinks = linkButtons.length > 0
    const hasSocials = socialLinks.some(s => s.url && s.url.length > 0)

    const { uploadVideo } = useContext(MediaContext)
    const isMobile = useMediaQuery('(max-width:1050px)')

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 10,
                distance: 5
            }
        })
    )

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const oldIndex = linkButtons.findIndex(lb => lb.uuid === active.id)
        const newIndex = linkButtons.findIndex(lb => lb.uuid === over.id)

        if (oldIndex !== -1 && newIndex !== -1) {
            const newLinkButtons = arrayMove([...linkButtons], oldIndex, newIndex)
            handleReorderLinks(newLinkButtons)
        }
    }, [linkButtons, handleReorderLinks])

    const actionBtnStyle = {
        m: 0,
        textTransform: "none",
        flex: 1,
        fontWeight: 600,
        borderRadius: '40px',
        boxShadow: "4px 5px 0px #000",
    }

    const uploadBtnStyle = {
        m: 0,
        textTransform: "none",
        flex: 1,
        fontWeight: 600,
        borderRadius: '40px',
        padding: '.6rem',
        boxShadow: "4px 5px 0px #000",
    }

    return (
        <Box sx={{ width: '100%' }}>
            {user.hasActiveTier ?
                <>
                    <Box sx={{
                        display: "flex",
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: "space-evenly",
                        marginBottom: '1rem',
                        width: '100%',
                    }}>
                        <Button
                            variant="contained"
                            sx={uploadBtnStyle}
                            onClick={uploadVideo}
                            startIcon={<Video />}
                        >
                            Upload Video
                        </Button>
                    </Box>
                    <Box sx={{
                        display: "flex",
                        flexDirection: 'row',
                        gap: 2,
                        justifyContent: "space-evenly",
                        marginBottom: '2rem',
                        width: '100%',
                    }}>
                        <Button
                            variant="contained"
                            sx={actionBtnStyle}
                            onClick={() => setOpenLinkModal(true)}
                            startIcon={<AddLinkIcon />}
                        >
                            Add Links
                        </Button>
                        <Button
                            variant="contained"
                            sx={actionBtnStyle}
                            onClick={() => setOpenIconModal(true)}
                            startIcon={<InstagramIcon />}
                        >
                            Add Socials
                        </Button>
                    </Box>

                    <SocialIconsList socials={socialLinks} />

                    <Box sx={{
                        m: 0,
                        p: 0,
                    }}>
                        {!hasLinks && !hasSocials && (
                            <Typography
                                variant="body1"
                                component="p"
                                sx={{
                                    fontWeight: 400,
                                    textAlign: "center",
                                    m: "3rem 0 0 0"
                                }}>
                                Use the buttons above to add your links and socials
                            </Typography>
                        )}

                        {!hasLinks && hasSocials && (
                            <Typography
                                variant="body1"
                                component="p"
                                sx={{
                                    fontWeight: 400,
                                    textAlign: "center",
                                    m: "3rem 0 0 0"
                                }}>
                                Your links will appear here.
                            </Typography>
                        )}

                        <DndContext
                            onDragEnd={handleDragEnd}
                            collisionDetection={closestCorners}
                            sensors={sensors}
                        >
                            <SortableContext
                                items={linkButtons.map(lb => lb.uuid)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Box>
                                    {linkButtons.map(lb => (
                                        <LinkButtonStudio
                                            key={lb.uuid}
                                            {...lb}
                                            onUpdate={handleUpdateLink}
                                            onDelete={handleDeleteLink}
                                            moneyType={moneyLinks[lb.uuid] || ''}
                                            onSetMoneyType={handleSetMoneyType}
                                            isPro={isPro}
                                        />
                                    ))}
                                </Box>
                            </SortableContext>
                        </DndContext>
                    </Box>

                    <AddLinkButonModal
                        open={openLinkModal}
                        onClose={() => setOpenLinkModal(false)}
                        onAddLink={handleAddLink}
                        isPro={isPro}
                        onSetMoneyType={handleSetMoneyType}
                    />
                    <SocialLinkModal
                        open={openIconModal}
                        onAddIcon={handleSocialIconChange}
                        onClose={() => setOpenIconModal(false)}
                        pageId={pageId}
                    />
                </>
                :
                <SubscriptionAlert />
            }
        </Box>
    )
}
